import { serviceArrivalScheduleClock } from './waterJourney';

/**
 * Operator Desk grocery / tacos (food catering) journey — structured timeline
 * parity with water/WM/haul. Both lines share store/vendor → pad pickup runs.
 */

export type FoodDeskServiceLine = 'grocery' | 'tacos';

export type FoodDeliveryProofPhoto = {
  stage: string;
  kind: string;
  url: string;
  at: string | null;
};

export type FoodDeliveryJourney = {
  id: string;
  workId: string;
  serviceLine: FoodDeskServiceLine;
  title: string;
  status: string;
  statusLabel: string;
  contractorName: string | null;
  driverName: string | null;
  pickupStore: string | null;
  createdAt: string | null;
  paidAt: string | null;
  readyForPickupAt: string | null;
  claimedAt: string | null;
  enRouteAt: string | null;
  /** On-site / delivered clock (Attention arrival). */
  arrivedAt: string | null;
  deliveredAt: string | null;
  confirmedAt: string | null;
  cancelledAt: string | null;
  preferredTiming: string | null;
  /** Clock form of preferred delivery window for Attention schedule kinds. */
  scheduledAt: string | null;
  /** Haul DELIVERY_* load/unload proofs (grocery/tacos driver captures). */
  proofPhotos: FoodDeliveryProofPhoto[];
};

export type FoodDeliveryJourneyStep = {
  id: string;
  label: string;
  at: string | null;
  state: 'complete' | 'active' | 'locked';
  photos: FoodDeliveryProofPhoto[];
};

/** True for guest grocery/tacos and haul grocery/tacos_deliver rows. */
export function isFoodDeskServiceLine(raw: unknown): boolean {
  const s = String(raw || '').trim().toLowerCase();
  return s === 'grocery' || s === 'groceries' || s === 'grocery_delivery'
    || s === 'tacos' || s === 'taco' || s === 'food' || s === 'tacos_deliver' || s === 'catering';
}

export function foodDeskServiceLine(raw: unknown): FoodDeskServiceLine {
  const s = String(raw || '').trim().toLowerCase();
  if (s.includes('groc')) return 'grocery';
  return 'tacos';
}

/**
 * preferred_timing is often a human label ("Thu, Aug 7 · Late morning").
 * Prefer explicit quote_inputs.deliveryDateIso, then ISO prefixes, then raw clocks.
 */
export function foodArrivalScheduleClock(
  preferredTiming: unknown,
  quoteInputs: unknown = null,
): string | null {
  let inputs: Record<string, unknown> | null = null;
  if (quoteInputs && typeof quoteInputs === 'object' && !Array.isArray(quoteInputs)) {
    inputs = quoteInputs as Record<string, unknown>;
  } else if (typeof quoteInputs === 'string' && quoteInputs.trim()) {
    try {
      const parsed = JSON.parse(quoteInputs) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        inputs = parsed as Record<string, unknown>;
      }
    } catch { /* older free-text */ }
  }
  const fromInputs = serviceArrivalScheduleClock(
    inputs?.deliveryDateIso ?? inputs?.delivery_date ?? inputs?.need_by ?? inputs?.needBy,
  );
  if (fromInputs) return fromInputs;

  const timing = String(preferredTiming || '').trim();
  if (!timing) return null;
  const direct = serviceArrivalScheduleClock(timing);
  if (direct) return direct;
  const iso = timing.match(/\b(\d{4}-\d{2}-\d{2})\b/);
  if (iso) return serviceArrivalScheduleClock(iso[1]);
  return null;
}

export function foodJourneyLabel(
  job: Pick<FoodDeliveryJourney, 'status' | 'paidAt' | 'readyForPickupAt' | 'claimedAt' | 'enRouteAt' | 'deliveredAt' | 'confirmedAt' | 'cancelledAt'>,
): string {
  const status = String(job.status || '').toLowerCase();
  if (status === 'cancelled' || status === 'canceled' || job.cancelledAt) return 'Cancelled';
  if (status === 'completed' || status === 'complete' || job.confirmedAt) return 'Confirmed';
  if (status === 'awaiting_confirm' || job.deliveredAt) return 'Delivered';
  if (status === 'rolling' || status === 'en_route' || job.enRouteAt) return 'En route';
  if (status === 'claimed' || job.claimedAt) return 'Accepted';
  if (status === 'ready_for_pickup' || job.readyForPickupAt) return 'Ready for pickup';
  if (status === 'paid' || job.paidAt) return 'Paid';
  if (status === 'awaiting_payment' || status === 'pending_pay') return 'Awaiting payment';
  if (status === 'open' || status === 'pending') return 'Requested';
  return 'Requested';
}

export function foodJourneySteps(job: FoodDeliveryJourney): FoodDeliveryJourneyStep[] {
  const rows = [
    { id: 'requested', label: 'Requested', at: job.createdAt },
    { id: 'paid', label: 'Paid', at: job.paidAt },
    { id: 'ready', label: 'Ready for pickup', at: job.readyForPickupAt },
    { id: 'accepted', label: 'Accepted', at: job.claimedAt },
    { id: 'en_route', label: 'En route', at: job.enRouteAt },
    { id: 'delivered', label: 'Delivered', at: job.deliveredAt },
    { id: 'confirmed', label: 'Operator confirmed', at: job.confirmedAt },
  ];
  const filtered = rows.filter((row) => {
    if (row.id === 'paid') {
      return Boolean(row.at) || Boolean(job.readyForPickupAt || job.claimedAt || job.enRouteAt || job.deliveredAt);
    }
    if (row.id === 'ready') {
      return Boolean(row.at)
        || Boolean(job.claimedAt || job.enRouteAt || job.deliveredAt)
        || String(job.status || '').toLowerCase() === 'ready_for_pickup';
    }
    if (row.id === 'confirmed') return Boolean(row.at) || Boolean(job.deliveredAt);
    return true;
  });
  const terminal = ['completed', 'complete', 'cancelled', 'canceled'].includes(String(job.status || '').toLowerCase())
    || Boolean(job.cancelledAt);
  const next = filtered.findIndex((row) => !row.at);
  const proofs = job.proofPhotos || [];
  return filtered.map((row, index) => ({
    ...row,
    state: (row.at
      ? 'complete'
      : index === next && !terminal
        ? 'active'
        : 'locked') as 'complete' | 'active' | 'locked',
    // Load proofs = order collected/packed after accept; unload = delivered placement.
    photos: proofs.filter((photo) => (
      row.id === 'accepted' ? photo.stage === 'load'
        : row.id === 'delivered' ? photo.stage === 'unload'
          : false
    )),
  }));
}

function field(
  row: Record<string, unknown>,
  guest: Record<string, unknown> | null | undefined,
  key: string,
): string | null {
  const primary = row[key];
  if (primary != null && String(primary).trim()) return String(primary);
  const secondary = guest?.[key];
  if (secondary != null && String(secondary).trim()) return String(secondary);
  return null;
}

export function buildFoodDeliveryJourney(
  row: Record<string, unknown>,
  extras?: {
    guest?: Record<string, unknown> | null;
    geoEvents?: Array<{ type?: unknown; at?: unknown }>;
    contractorName?: string | null;
    driverName?: string | null;
    workSource?: 'guest' | 'haul';
    proofPhotos?: FoodDeliveryProofPhoto[];
  },
): FoodDeliveryJourney {
  const guest = extras?.guest || null;
  const id = String(row.id);
  const serviceLine = foodDeskServiceLine(row.service_line || guest?.service_line || row.serviceLine);
  const status = String(row.status || guest?.status || 'pending');
  const geo = (type: string) => {
    const hit = (extras?.geoEvents || []).find((event) => String(event.type || '') === type);
    return hit?.at != null ? String(hit.at) : null;
  };
  const createdAt = field(row, guest, 'created_at') || field(row, guest, 'requested_at');
  const explicitPaidAt = field(row, guest, 'paid_at');
  const paymentPaid = String(guest?.payment_status || row.payment_status || '').toLowerCase() === 'paid';
  const paidAt = explicitPaidAt || (paymentPaid ? createdAt : null);
  const readyForPickupAt = field(row, guest, 'ready_for_pickup_at') || field(row, null, 'ready_at');
  const claimedAt = field(row, null, 'claimed_at');
  const enRouteAt = field(row, null, 'rolled_at') || field(row, null, 'en_route_at') || geo('departed_pickup');
  const deliveredAt = field(row, null, 'partner_delivered_at')
    || field(row, null, 'delivered_at')
    || geo('arrived_dropoff');
  const confirmedAt = field(row, null, 'completed_at') || field(row, null, 'customer_confirmed_at');
  const cancelledAt = field(row, guest, 'cancelled_at');
  const preferredTiming = field(row, guest, 'preferred_timing');
  const quoteInputs = row.quote_inputs ?? guest?.quote_inputs ?? null;
  const workSource = extras?.workSource || (row.guest_request_id != null ? 'haul' : 'guest');
  const journey: FoodDeliveryJourney = {
    id,
    workId: `${workSource}:${id}`,
    serviceLine,
    title: String(
      row.title
      || row.well_or_site
      || row.well_name
      || guest?.well_name
      || guest?.well_or_site
      || (serviceLine === 'grocery' ? 'Grocery' : 'Tacos'),
    ),
    status,
    statusLabel: '',
    contractorName: extras?.contractorName ?? null,
    driverName: extras?.driverName ?? null,
    pickupStore: field(row, guest, 'pickup_store') || field(row, null, 'pickup_label'),
    createdAt,
    paidAt,
    readyForPickupAt,
    claimedAt,
    enRouteAt,
    arrivedAt: deliveredAt,
    deliveredAt,
    confirmedAt,
    cancelledAt,
    preferredTiming,
    scheduledAt: foodArrivalScheduleClock(preferredTiming, quoteInputs),
    proofPhotos: extras?.proofPhotos || [],
  };
  journey.statusLabel = foodJourneyLabel(journey);
  return journey;
}
